/**
 * Encapsulates the full J-code delete flow used by the CPT codes page:
 * issuing the request, refreshing auth-on-file data on success, surfacing
 * toasts, syncing the in-progress flag to the modal's Redux metadata, and
 * exposing cancel-aware handlers ready for the
 * `DeleteConfirmationModal`'s prop API.
 *
 * @module useJCodeDeleteFlow
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { GeneralOrderDetails } from "../../../../../data-model/medicalPaOrdersModel";
import { closeModal, setMetaData } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { useDeleteJCode } from "./useDeleteJCode";

/** Handlers returned by {@link useJCodeDeleteFlow}. */
interface JCodeDeleteFlowHandlers {
  /** Issues the delete and flips the modal's Redux `isDeleting` flag. */
  handleDelete: (itemId: string) => void;
  /** Aborts any in-flight delete and closes the confirmation modal. */
  handleCancel: () => void;
}

/**
 * React hook that wires the J-code delete flow to the confirmation
 * modal. Internally uses `useDeleteJCode` for abort-aware fetching and
 * dispatches `closeModal` / `setMetaData` so the modal's loading and
 * lifecycle stay in sync.
 *
 * @param generalOrderDetails - Order context used to compose the
 *   delete payload (order_id and org_id).
 * @returns Memo-stable handlers for the modal's `onDeleteClick`,
 *   `onCancelClick`, and `onCloseClick` props.
 */
export function useJCodeDeleteFlow(
  generalOrderDetails: GeneralOrderDetails | null | undefined,
): JCodeDeleteFlowHandlers {
  const dispatch = useDispatch<AppDispatch>();
  const metaData = useSelector((state: RootState) => state.modal.metaData);
  const { fetchAuthOnFileDetails } = useDataAccumulatorForSingleOrder();

  const { triggerDelete, abort } = useDeleteJCode({
    onSuccess: async () => {
      await fetchAuthOnFileDetails(
        generalOrderDetails?.order_id ?? "",
        getOrgIdForFetchExternalWorklist() ?? "",
      );
      dispatch(closeModal());
      toast.success(ToastMessages.CPT_CODE_DELETED_SUCCESS);
    },
    onFailure: () => {
      toast.error(ToastMessages.CPT_CODE_DELETED_ERROR);
    },
  });

  const handleDelete = (itemId: string) => {
    triggerDelete({
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() ?? "",
      j_code_id: itemId ?? "",
    });
    dispatch(setMetaData({ ...metaData, isDeleting: true }));
  };

  const handleCancel = () => {
    dispatch(closeModal());
    abort();
  };

  return { handleDelete, handleCancel };
}
